/**
 * A flow definition, drawn (#1736).
 *
 * ── what it is ──────────────────────────────────────────────────────────────
 * #1736 asks for a *"calm branch-aware route map: stages are stations,
 * transitions are lines, branches stay legible rather than becoming a force
 * graph"*, and insists that *"outline/list and keyboard mode is equally
 * complete; the map is not the only way to author"*.
 *
 * Both views render `flowMap()` — one derivation, two renderings. That is the
 * whole reason the layout is a pure function in a separate module: an outline
 * computed independently would agree with the map until the day it did not,
 * and the disagreement would be invisible to everyone except the person whose
 * branch was drawn wrong.
 *
 * ── read-only, deliberately ────────────────────────────────────────────────
 * This draws a definition; it does not author one. Authoring needs the
 * participant-role catalogue, runtime bindings and operational doors #1736
 * specifies, and those are core work. Drawing what the platform already
 * publishes needs nothing new: `core.describe_flow` has returned every stage,
 * transition, gate, timer, subflow, condition and action list since
 * `2026081002`, and the console has been reading it all along to answer one
 * question — which manual edges leave the current stage — and drawing none of
 * it.
 *
 * ── no SVG, and that is a choice ───────────────────────────────────────────
 * Lines are named on their station ("→ approved, when …"), not drawn as paths.
 * A path is invisible to a screen reader and unselectable by a keyboard, and
 * this surface has to be equally complete both ways. Columns carry the shape;
 * the text carries the meaning.
 */
import { useId, useState } from 'react'
import { cx } from '../controls/cx'
import { flowMap, type FlowMapStage, type FlowMapTransition } from './flowMap'
import s from './FlowRouteMap.module.css'

export interface FlowRouteMapProps {
  stages: readonly FlowMapStage[]
  transitions: readonly FlowMapTransition[]
  /** The machine's `is_initial` stage, so an orphan is not read as a start. */
  entry?: string | null
  /** Human label per stage code, from the status machine. Falls back to the code. */
  labelOf?: (code: string) => string
  /** The selected stage, if the host is driving selection. */
  selected?: string | null
  onSelect?: (code: string) => void
  testId?: string
}

/** What each station says about itself, beyond its name. */
function stageNotes(stage: FlowMapStage | undefined): string[] {
  if (!stage) return []
  const notes: string[] = []
  if (stage.kind && stage.kind !== 'normal') notes.push(stage.kind)
  // Presence, not contents: a gate's conditions are evaluated by the server and
  // interpreting them here would be a second implementation of the gate.
  if (stage.gate != null) notes.push('gate')
  if (Array.isArray(stage.timers) ? stage.timers.length > 0 : stage.timers != null) notes.push('timer')
  if (stage.sub_flow) notes.push(`subflow: ${stage.sub_flow}`)
  if (stage.advance_action) notes.push('advance action')
  return notes
}

export function FlowRouteMap({
  stages, transitions, entry, labelOf, selected, onSelect, testId,
}: FlowRouteMapProps) {
  const [mode, setMode] = useState<'map' | 'outline'>('map')
  const headingId = useId()
  const map = flowMap(stages, transitions, entry)
  const name = (code: string) => labelOf?.(code) ?? code
  const byCode = new Map(stages.map((st) => [st.code, st]))

  if (map.nodes.length === 0) {
    return (
      <p className={s.empty} data-testid={testId ? `${testId}-empty` : undefined}>
        This definition declares no stages.
      </p>
    )
  }

  /** The edges leaving one station, in the order the definition declares them. */
  const out = (code: string) => map.edges.filter((e) => e.from === code && !e.dangling)

  const station = (code: string) => {
    const node = map.nodes.find((n) => n.code === code)!
    const notes = stageNotes(byCode.get(code))
    const leaving = out(code)
    return (
      <button
        type="button"
        key={code}
        className={s.station}
        data-testid={`flow-station-${code}`}
        data-selected={selected === code || undefined}
        data-unreachable={node.unreachable || undefined}
        aria-current={selected === code ? 'true' : undefined}
        onClick={() => onSelect?.(code)}
      >
        <span className={s.stationCode}>{name(code)}</span>
        {(notes.length > 0 || node.unreachable) && (
          <span className={s.stationMeta}>
            {node.unreachable && <span>nothing reaches this stage</span>}
            {notes.map((n) => <span key={n}>{n}</span>)}
          </span>
        )}
        {leaving.length > 0 && (
          <span className={s.out}>
            {leaving.map((e) => (
              <span key={`${e.from}->${e.to}`} className={cx(s.edge, e.back && s.edgeBack)}>
                <span>{e.back ? '↩' : '→'} {name(e.to)}</span>
                {e.trigger && <span>{e.trigger}</span>}
              </span>
            ))}
          </span>
        )}
      </button>
    )
  }

  const columns = Array.from({ length: map.columns }, (_, i) =>
    map.nodes.filter((n) => n.column === i).sort((a, b) => a.row - b.row))

  return (
    <section className={s.map} aria-labelledby={headingId} data-testid={testId}>
      <div className={s.modes}>
        <h3 id={headingId} className={s.modeNote}>
          {map.nodes.length} stages · {map.edges.length - map.dangling.length} transitions
        </h3>
        {/* A toggle, not a fallback. The outline is the map's equal and says so
            by being one press away rather than a preference somewhere else. */}
        <button
          type="button"
          data-testid={testId ? `${testId}-mode` : undefined}
          onClick={() => setMode((m) => (m === 'map' ? 'outline' : 'map'))}
          aria-pressed={mode === 'outline'}
        >
          {mode === 'map' ? 'Show as outline' : 'Show as map'}
        </button>
      </div>

      {mode === 'map' ? (
        <div className={s.frame}>
          <div className={s.columns} data-testid={testId ? `${testId}-columns` : undefined}>
            {columns.map((nodes, i) => (
              <div className={s.column} key={i}>
                <div className={s.columnHead}>{i === 0 ? 'Start' : `Step ${i + 1}`}</div>
                {nodes.map((n) => station(n.code))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* The SAME nodes and edges, in reading order. A list is what a screen
           reader and a keyboard want anyway, so this is not a lesser view. */
        <ol className={s.outline} data-testid={testId ? `${testId}-outline` : undefined}>
          {map.nodes
            .slice()
            .sort((a, b) => a.column - b.column || a.row - b.row)
            .map((n) => <li key={n.code}>{station(n.code)}</li>)}
        </ol>
      )}

      {map.dangling.length > 0 && (
        /* A transition naming a stage the definition does not declare. Said out
           loud rather than dropped: this is the surface built to show
           definitions, and silently hiding the broken part of one is the worst
           thing it could do. */
        <p className={s.dangling} role="alert" data-testid={testId ? `${testId}-dangling` : undefined}>
          {map.dangling.length === 1 ? 'One transition names' : `${map.dangling.length} transitions name`}{' '}
          a stage this definition does not declare:{' '}
          {map.dangling.map((e) => `${e.from} → ${e.to}`).join(', ')}.
        </p>
      )}
    </section>
  )
}
